import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useLingui } from "@lingui/react/macro";
import { employeeName, type IScheduledShift } from "@medoma/models/shifts";
import { CompetenceChip } from "@/components/Chips/CompetenceChip";
import { unassignGroupFromShift } from "@/api/Shifts";
import { deallocateShiftsFromActivityOccurrence } from "@/api/Activities";
import { deducedError, displayErrorMessageAlert } from "@/Utils/ErrorUtils";
import { useUserProfile } from "@/api/Users";
import type { AssignShiftTarget } from "@/components/AssignShiftAction/AssignShiftAction";

export const AssigneesChips = ({
  assignees,
  target,
  canManageAssignees,
}: {
  assignees: IScheduledShift[];
  target: AssignShiftTarget;
  canManageAssignees: boolean | undefined;
}) => {
  const { countryCode } = useUserProfile();
  const { t } = useLingui();
  const queryClient = useQueryClient();

  const { mutate: unassignShift, isPending: isUnassigningShift } = useMutation({
    mutationFn: (shiftId: number) =>
      target.kind === "group"
        ? unassignGroupFromShift(target.groupId, shiftId)
        : deallocateShiftsFromActivityOccurrence(
            [shiftId],
            target.activityId,
            target.occurrenceId,
          ),
    onSuccess: () => queryClient.invalidateQueries(),
    onError: (error) => {
      const message = (() => {
        switch (target.kind) {
          case "group":
            return t`Det gick inte att ta bort medarbetaren från besöket.`;
          case "occurrence":
            return t`Det gick inte att ta bort medarbetaren från aktiviteten.`;
          default:
            throw target satisfies never;
        }
      })();
      displayErrorMessageAlert(`${message} ${deducedError(error)}`);
    },
  });

  const confirmUnassignShift = (shift: IScheduledShift) => {
    const name = shift.employee
      ? employeeName(shift.employee, { countryCode, viewer: "colleague" })
      : t`medarbetaren`;
    const message = (() => {
      switch (target.kind) {
        case "group":
          return t`Är du säker på att du vill ta bort ${name} från besöket?`;
        case "occurrence":
          return t`Är du säker på att du vill ta bort ${name} från aktiviteten?`;
        default:
          throw target satisfies never;
      }
    })();
    if (window.confirm(message)) {
      unassignShift(shift.id);
    }
  };

  return (
    <>
      {assignees.map((shift) => (
        <CompetenceChip
          key={shift.id}
          competence={shift.competence}
          state={canManageAssignees ? "assigned" : "neutral"}
          size="small"
          onRemove={
            canManageAssignees ? () => confirmUnassignShift(shift) : undefined
          }
          disabled={isUnassigningShift}
          content={
            shift.employee
              ? employeeName(shift.employee, {
                  countryCode,
                  viewer: "colleague",
                })
              : undefined
          }
        />
      ))}
    </>
  );
};
